import { Component, inject, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ParkingZoneService } from '../../../api/gen/api/parkingZone.service';
import { CreateNewParkingZoneRequest, GovernanceType, ParkingZoneAddressDto } from '../../../api/gen';
import { JJamesAlertComponent, JJamesCardComponent, JjamesButtonComponent, JJamesInputComponent } from 'shared-lib';

@Component({
  selector: 'app-create-parking-zone',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    JjamesButtonComponent,
    JJamesInputComponent,
    JJamesCardComponent,
    JJamesAlertComponent
  ],
  templateUrl: './create-parking-zone.component.html',
  styleUrls: ['./create-parking-zone.component.scss']
})
export class CreateParkingZoneComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private parkingZoneService = inject(ParkingZoneService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  createParkingZoneForm: FormGroup;
  isSubmitting = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  parkingSpaceId: string | null = null;

  governanceTypes = Object.values(GovernanceType);

  constructor() {
    this.createParkingZoneForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      description: ['', [Validators.maxLength(500)]],
      governanceType: [GovernanceType.Internal, [Validators.required]],
      capacity: [null, [Validators.min(1), Validators.max(10000)]],
      // Address fields
      firstAddressLine: ['', [Validators.maxLength(100)]],
      secondAddressLine: ['', [Validators.maxLength(100)]],
      locality: ['', [Validators.maxLength(50)]],
      postCode: ['', [Validators.maxLength(20)]],
      region: ['', [Validators.maxLength(50)]],
      countryCode: ['', [Validators.minLength(2), Validators.maxLength(3)]],
      latitude: [null, [Validators.min(-90), Validators.max(90)]],
      longitude: [null, [Validators.min(-180), Validators.max(180)]],
      // Optional fields
      externalOrganizationId: [''],
      zoneInfrastructureId: ['']
    });
  }

  ngOnInit(): void {
    // Get parking space ID from route parameters
    this.parkingSpaceId = this.route.snapshot.paramMap.get('parkingSpaceId');
  }

  onSubmit(): void {
    if (this.createParkingZoneForm.valid && !this.isSubmitting && this.parkingSpaceId) {
      this.isSubmitting = true;
      this.errorMessage = null;
      this.successMessage = null;

      const formValue = this.createParkingZoneForm.value;

      const address: ParkingZoneAddressDto = {
        firstAddressLine: formValue.firstAddressLine || null,
        secondAddressLine: formValue.secondAddressLine || null,
        locality: formValue.locality || null,
        postCode: formValue.postCode || null,
        region: formValue.region || null,
        countryCode: formValue.countryCode || null,
        latitude: formValue.latitude || null,
        longitude: formValue.longitude || null
      };

      const createRequest: CreateNewParkingZoneRequest = {
        name: formValue.name,
        description: formValue.description || null,
        parkingSpaceId: this.parkingSpaceId,
        governanceType: formValue.governanceType,
        address: address,
        capacity: formValue.capacity || undefined,
        externalOrganizationId: formValue.externalOrganizationId || null,
        zoneInfrastructureId: formValue.zoneInfrastructureId || null
      };

      this.parkingZoneService.parkingZonePost(createRequest).subscribe({
        next: (response) => {

          if (response) {
            this.successMessage = 'Parking zone created successfully!';
            setTimeout(() => {
              this.isSubmitting = false;
              this.router.navigate(['/parking-space']);
            }, 2500); // Show success message briefly before navigating
          }
        },
        error: (error) => {
          this.isSubmitting = false;
          this.errorMessage = error.error.title || 'An error occurred while creating the parking zone. Please try again.';
          console.error('Error creating parking zone:', error);
        }
      });
    } else {
      // Mark all fields as touched to show validation errors
      this.markFormGroupTouched(this.createParkingZoneForm);
    }
  }

  onCancel(): void {
    this.router.navigate(['/parking-space']);
  }

  onClearError(): void {
    this.errorMessage = null;
  }

  onClearSuccess(): void {
    this.successMessage = null;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      control?.markAsTouched({ onlySelf: true });
    });
  }

  // Helper methods for template
  isFieldInvalid(fieldName: string): boolean {
    const field = this.createParkingZoneForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  getFieldError(fieldName: string): string {
    const field = this.createParkingZoneForm.get(fieldName);
    if (field && field.errors && field.touched) {
      if (field.errors['required']) return `${this.getFieldLabel(fieldName)} is required`;
      if (field.errors['minlength']) return `${this.getFieldLabel(fieldName)} is too short`;
      if (field.errors['maxlength']) return `${this.getFieldLabel(fieldName)} is too long`;
      if (field.errors['min']) return `${this.getFieldLabel(fieldName)} value is too low`;
      if (field.errors['max']) return `${this.getFieldLabel(fieldName)} value is too high`;
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      name: 'Name',
      description: 'Description',
      governanceType: 'Governance Type',
      capacity: 'Capacity',
      firstAddressLine: 'Address Line 1',
      secondAddressLine: 'Address Line 2',
      locality: 'City/Locality',
      postCode: 'Postal Code',
      region: 'Region/State',
      countryCode: 'Country Code',
      latitude: 'Latitude',
      longitude: 'Longitude'
    };
    return labels[fieldName] || fieldName;
  }
}
